import axios from "axios";
import constants from "../../Constants";
import util from "../../util";

export const MEDIA = "https://media.qiktruck.co.za";

const baseURL = constants.baseURL;

// Unicode dash variants (em dash, en dash, minus sign, etc.) → ASCII hyphen.
const DASH_CHARS = /[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g;

export function normaliseDashes(text) {
  if (typeof text !== "string") return "";
  return text.replace(DASH_CHARS, "-");
}

/**
 * Pull chip Q&A rows out of the description textarea. Mirrors the API's
 * splitDescriptionAndAnswers so the preview shows only the customer's words.
 */
export function splitDescriptionAndAnswers(description) {
  if (!description) return { realDescription: "", priorAnswers: [] };
  const lines = description.split("\n");
  const realLines = [];
  const priorAnswers = [];
  for (const rawLine of lines) {
    const line = rawLine.trimStart();
    if (line.startsWith("- ")) {
      const body = line.slice(2).trim();
      const qIdx = body.indexOf("?");
      if (qIdx > 0 && qIdx < body.length - 1) {
        const question = body.slice(0, qIdx + 1).trim();
        const answer = body.slice(qIdx + 1).trim();
        if (question && answer) {
          priorAnswers.push({ question, answer });
          continue;
        }
      }
    }
    if (rawLine.trim()) realLines.push(rawLine);
  }
  return {
    realDescription: realLines.join("\n").trim(),
    priorAnswers,
  };
}

/** Deterministic "Quoted with" line from the priced quote snapshot. */
export function formatQuotedWithLine({
  quotedRunners = 0,
  quotedFloors = 0,
  quotedCollectiontime = "",
} = {}) {
  const parts = [];
  if (quotedRunners > 0) {
    parts.push(
      `${quotedRunners} helper${quotedRunners === 1 ? "" : "s"}`,
    );
  }
  if (quotedFloors > 0) {
    parts.push(`${quotedFloors} floor${quotedFloors === 1 ? "" : "s"}`);
  }
  if (quotedCollectiontime) {
    parts.push(`pickup ${quotedCollectiontime}`);
  }
  return parts.length ? parts.join(" · ") : "";
}

/** Human-readable drive time: minutes, hours+minutes, or days when relevant. */
export function formatDriveDuration(minutes, { approximate = true } = {}) {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  const prefix = approximate ? "~" : "";
  if (m < 60) return `${prefix}${m} min`;

  const days = Math.floor(m / 1440);
  const remAfterDays = m % 1440;
  const hours = Math.floor(remAfterDays / 60);
  const mins = remAfterDays % 60;

  if (days > 0) {
    const dayLabel = days === 1 ? "1 day" : `${days} days`;
    if (hours > 0 && mins > 0) {
      return `${prefix}${dayLabel} ${hours} hr ${mins} min`;
    }
    if (hours > 0) return `${prefix}${dayLabel} ${hours} hr`;
    if (mins > 0) return `${prefix}${dayLabel} ${mins} min`;
    return `${prefix}${dayLabel}`;
  }

  if (mins === 0) return `${prefix}${hours} hr`;
  return `${prefix}${hours} hr ${mins} min`;
}

/** Traffic delay label, e.g. "+48 min" or "+1 hr 15 min vs off-peak". */
export function formatDriveDelta(minutes) {
  const core = formatDriveDuration(minutes, { approximate: false });
  return `+${core} vs off-peak`;
}

/** Strip dash variants from every user-facing string in an AI response. */
export function sanitiseAiResponse(data) {
  if (!data || typeof data !== "object") return data;
  return {
    ...data,
    recommendedReason: normaliseDashes(data.recommendedReason || ""),
    questions: (data.questions || []).map((q) => ({
      ...q,
      text: normaliseDashes(q?.text || ""),
      chips: (q?.chips || []).map((c) => normaliseDashes(c)),
    })),
    tips: (data.tips || []).map((t) => ({
      ...t,
      text: normaliseDashes(t?.text || ""),
    })),
    weather: data.weather
      ? {
          ...data.weather,
          whenLabel: normaliseDashes(data.weather.whenLabel || ""),
          condition: normaliseDashes(data.weather.condition || ""),
          alert: data.weather.alert
            ? normaliseDashes(data.weather.alert)
            : data.weather.alert,
        }
      : data.weather,
    traffic: data.traffic
      ? {
          ...data.traffic,
          whenLabel: normaliseDashes(data.traffic.whenLabel || ""),
          alert: data.traffic.alert
            ? normaliseDashes(data.traffic.alert)
            : data.traffic.alert,
          betterPickup: data.traffic.betterPickup
            ? {
                ...data.traffic.betterPickup,
                whenLabel: normaliseDashes(data.traffic.betterPickup.whenLabel || ""),
              }
            : data.traffic.betterPickup,
        }
      : data.traffic,
    loadEstimate: data.loadEstimate
      ? {
          ...data.loadEstimate,
          itemSummary: data.loadEstimate.itemSummary
            ? normaliseDashes(data.loadEstimate.itemSummary)
            : data.loadEstimate.itemSummary,
        }
      : data.loadEstimate,
    nearbyAvailability: data.nearbyAvailability
      ? {
          ...data.nearbyAvailability,
          note: data.nearbyAvailability.note
            ? normaliseDashes(data.nearbyAvailability.note)
            : data.nearbyAvailability.note,
        }
      : data.nearbyAvailability,
  };
}

export function getTruckImage(trucktype) {
  return `${MEDIA}/3d-${trucktype}.webp`;
}

export function cleanTruckLabel(trucktype = "") {
  return trucktype.replace("open", "").replace("closed", "").trim();
}

export function cleanTruckBodyLabel(trucktype = "") {
  return trucktype.replace("open", "Open").replace("closed", "Closed").trim();
}
function roundToNearestFifty(num) {
  return Math.round(num / 50) * 50;
}

/**
 * Indicative "from" price for a vehicle card. Mirrors the calculation used on
 * the original landing page so the displayed estimate stays consistent.
 */
export function vehiclePriceTip(vehicle, change = 1) {
  const costOfDelivery =
    2000 * (vehicle?.pricePerMeterCents || 0) +
    1200 * (vehicle?.pricePerSecondCents || 0) +
    (vehicle?.handlingFeeCents || 0);
  const serviceFee = (vehicle?.serviceFeePercentage || 0) * costOfDelivery;
  return roundToNearestFifty(
    Math.round(((costOfDelivery + serviceFee) * 1.15 * change || 1) / 100),
  );
}

/** One representative card per loadgroup (sellable vehicles only). */
export function groupVehicles(vehicletypes = []) {
  const seen = new Set();
  return (vehicletypes || []).filter((v) => {
    if (!v.sell) return false;
    const group = v.loadgroup || v.type;
    if (seen.has(group)) return false;
    seen.add(group);
    return true;
  });
}

export function bodyTypesFor(vehicletypes = [], loadgroup) {
  return (vehicletypes || []).filter((v) => v.loadgroup === loadgroup);
}

export function findVehicle(vehicletypes = [], type) {
  return (vehicletypes || []).find((v) => v.type === type);
}

/** Human-readable load capacity, e.g. "up to 800 kg" / "up to 4 tons". */
export function capacityLabel(tonnes) {
  if (!tonnes) return "";
  if (tonnes < 1) return `up to ${Math.round(tonnes * 1000)} kg`;
  return `up to ${tonnes} ton${tonnes > 1 ? "s" : ""}`;
}

const SESSION_KEY = "lp2-quote-session-id";
const AI_HANDOFF_KEY = "lp2-ai-session-handoff";

export const AI_SESSION_IMPRESSION_CAP = 6;

export const AI_SESSION_HANDOFF_MESSAGE =
  "We've shared our best suggestions for this trip. Your quote is still valid - book this vehicle and our dispatch team will review your load and confirm details before we assign a driver.";

/** Stable id for one browser visit - ties /quote + AI events for RL datasets. */
export function getOrCreateQuoteSessionId(regenerate = false) {
  if (typeof window === "undefined") return "";
  try {
    if (regenerate) {
      const id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
      return id;
    }
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

/** Remember that this session hit the AI impression cap (skip further LLM calls). */
export function markSessionAiHandoff(sessionId) {
  if (!sessionId || typeof window === "undefined") return;
  try {
    sessionStorage.setItem(`${AI_HANDOFF_KEY}:${sessionId}`, "1");
  } catch {
    /* ignore */
  }
}

export function isSessionAiHandoff(sessionId) {
  if (!sessionId || typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(`${AI_HANDOFF_KEY}:${sessionId}`) === "1";
  } catch {
    return false;
  }
}

/** Same attribution string we send on /quote (navigator + UTM + referrer). */
export function buildQuoteSource_() {
  if (typeof document === "undefined") return "";
  return util.navigator_() + util.source_() + " qtref:" + (document?.referrer || "");
}

/** True when loaded on the dedicated merchant widget checkout (/widget/quote). */
export function isWidgetEmbed() {
  if (typeof window === "undefined") return false;
  try {
    return window.location.pathname === "/widget/quote";
  } catch {
    return false;
  }
}

export function getWidgetSiteId() {
  if (typeof window === "undefined") return "";
  try {
    return new URLSearchParams(window.location.search).get("site") || "";
  } catch {
    return "";
  }
}

/** postMessage bridge back to qiktruck-widget.js on the merchant page. */
export function notifyWidgetParent(type, payload = {}) {
  if (typeof window === "undefined" || window.parent === window) return;
  try {
    window.parent.postMessage({ type, ...payload }, "*");
  } catch {
    /* ignore */
  }
}

/** Fire-and-forget AI interaction event for RL / value measurement. */
export function trackAiSuggestionEvent(payload) {
  if (!payload?.eventType) return;
  axios
    .post(baseURL + "/qiktruck-caiaphas-intelligence/event", payload)
    .catch(() => {});
}

/** Remove tips that duplicate the weather forecast card. */
export function filterWeatherDuplicateTips(tips, weather) {
  if (!Array.isArray(tips) || !weather || weather.tooFar) return tips || [];
  return tips.filter((tip) => !isWeatherDuplicateTip(tip?.text, weather));
}

/** Remove tips that duplicate the traffic drive-time pill. */
export function filterTrafficDuplicateTips(tips, traffic) {
  if (!Array.isArray(tips) || !traffic) return tips || [];
  return tips.filter((tip) => !isTrafficDuplicateTip(tip?.text, traffic));
}

function isTrafficDuplicateTip(text, traffic) {
  if (!text || typeof text !== "string") return false;
  const t = text.toLowerCase().trim();
  const alert = (traffic.alert || "").toLowerCase();
  if (alert && overlapRatio(t, alert) > 0.45) return true;

  const trafficWords = [
    "congestion",
    "traffic jam",
    "rush hour",
    "off-peak",
    "drive time",
    "travel time",
    "commute",
  ];
  const mentionsTraffic = trafficWords.some((w) => t.includes(w))
    || /\d+\s*min(ute)?s?\s*(extra|longer|slow)/.test(t)
    || /allow extra time/.test(t);

  if (mentionsTraffic && (traffic.level === "heavy" || traffic.level === "moderate")) {
    return true;
  }
  if (traffic.deltaMin > 3 && /vs off-peak|slower than|\+?\s*\d+\s*min/.test(t)) {
    return true;
  }
  return false;
}

function isWeatherDuplicateTip(text, weather) {
  if (!text || typeof text !== "string") return false;
  const t = text.toLowerCase().trim();
  const alert = (weather.alert || "").toLowerCase();
  const condition = (weather.condition || "").toLowerCase();

  if (alert && overlapRatio(t, alert) > 0.45) return true;

  const weatherWords = ["drizzle", "rain", "storm", "thunder", "shower", "heatwave", "heat wave"];
  const mentionsWeather = weatherWords.some((w) => t.includes(w))
    || (condition && t.includes(condition));
  const bodyWeatherAdvice = /closed body|keeps? .* dry|protects? from|weather/.test(t);

  if ((weather.willRain || weather.severe) && mentionsWeather && bodyWeatherAdvice) {
    return true;
  }
  if (weather.temperatureC >= 35 && /heat|°c|hot/.test(t)) return true;
  if (/expected at (pickup|\d{1,2}:\d{2})/i.test(text) && mentionsWeather) return true;

  return false;
}

function overlapRatio(a, b) {
  if (!a || !b) return 0;
  const shorter = a.length <= b.length ? a : b;
  const longer = a.length > b.length ? a : b;
  if (shorter.length < 12) return longer.includes(shorter) ? 1 : 0;
  for (let len = Math.min(shorter.length, 40); len >= 12; len--) {
    if (longer.includes(shorter.slice(0, len))) return len / shorter.length;
  }
  return 0;
}
